// Pit Stop 4 gallery/ranking pure helpers (Figma 5411:5037 / 5417:10133).
// Everything here is side-effect free except the localStorage pair at the
// bottom, so the pagination/sorting rules stay unit-testable.

export const F1_GALLERY_PAGE_SIZE = 6
export const F1_RANKING_PAGE_SIZE = 6

// Backend codes for POST /rally/vote
export const F1_VOTE_ERROR = {
  alreadyVoted: 79,
  maxVotes: 83
}

export const F1_GALLERY_ANCHOR_ID = 'galeria'
export const F1_VOTED_STORAGE_KEY = 'f1_voted_photos'

export interface F1GalleryPhoto {
  id: number
  code: string
  name: string
  imageUrl: string
  votes: number
  profileId?: number
  statusId?: number
  isWinner?: boolean
  // Future backend fields: shown as the display name when non-empty
  profileName?: string
  nickName?: string
}

// The real endpoint (verified 2026-10-18) sends every numeric field as a
// string ("votes": "0"), which would corrupt sorting and vote increments
// ("0" + 1 === "01"), so raw payloads are coerced before any use.
export const normalizePhotos = (multimedias: any[] | null | undefined): F1GalleryPhoto[] =>
  (multimedias ?? []).map((raw) => ({
    ...raw,
    id: Number(raw?.id),
    votes: Number(raw?.votes) || 0,
    statusId: raw?.statusId === undefined || raw?.statusId === null ? undefined : Number(raw.statusId),
    profileId: raw?.profileId === undefined || raw?.profileId === null ? undefined : Number(raw.profileId),
    isWinner: raw?.isWinner === true || raw?.isWinner === 'true' || Number(raw?.isWinner) === 1,
    code: `${raw?.code ?? ''}`,
    name: `${raw?.name ?? ''}`,
    imageUrl: raw?.imageUrl ?? ''
  }))

/**
 * Display name for a photo: first non-empty of profileName/nickName/name.
 * The backend currently ships them empty, so the UI omits the name line
 * entirely when this returns ''.
 */
export const photoDisplayName = (photo: F1GalleryPhoto): string =>
  (photo.profileName || photo.nickName || photo.name || '').trim()

// statusId 4 is the moderation-rejected state (same convention the upload
// flow uses); photos without an imageUrl cannot render a card.
const MODERATION_REJECTED_STATUS = 4

export const selectVisiblePhotos = (multimedias: F1GalleryPhoto[] | null | undefined): F1GalleryPhoto[] =>
  (multimedias ?? []).filter(
    (photo) => Boolean(photo?.imageUrl) && photo?.statusId !== MODERATION_REJECTED_STATUS
  )

/** Sorted copy: votes desc, ties broken by id asc. Never mutates the input.
 * Coerces both fields so stray string payloads cannot corrupt the order. */
export const sortByVotes = (photos: F1GalleryPhoto[]): F1GalleryPhoto[] =>
  [...photos].sort(
    (a, b) => (Number(b.votes) - Number(a.votes)) || (Number(a.id) - Number(b.id))
  )

export const paginate = <T>(items: T[], page: number, size: number): T[] =>
  items.slice(page * size, page * size + size)

export const pageCount = (total: number, size: number): number =>
  size > 0 ? Math.ceil(total / size) : 0

/** Copy of the list with one extra vote on the given photo */
export const applyVote = (photos: F1GalleryPhoto[], id: number): F1GalleryPhoto[] =>
  photos.map((photo) => (photo.id === id ? { ...photo, votes: photo.votes + 1 } : photo))

export const readVotedIds = (): number[] => {
  try {
    const raw = window.localStorage.getItem(F1_VOTED_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'number') : []
  } catch (error) {
    return []
  }
}

export const markVoted = (id: number): number[] => {
  const voted = readVotedIds()
  const next = voted.includes(id) ? voted : [...voted, id]
  try {
    window.localStorage.setItem(F1_VOTED_STORAGE_KEY, JSON.stringify(next))
  } catch (error) {
    // Private mode / blocked storage: the in-memory state still carries it
  }
  return next
}

export const buildPhotoShareUrl = (origin: string, code: string): string =>
  `${origin}/pitstops?photo=${code}`
